import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@structural-climbing/auth'
import {
  Button,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Spinner,
  Tab,
  Tabs,
  softBadgeColors,
} from '@structural-climbing/ui'
import {
  competitionBoards,
  gradesForDiscipline,
  type CompetitionDiscipline,
  type Discipline,
} from '@structural-climbing/validation'
import { useGymGradeConfig } from '../../layouts/useGymGradeConfig'
import type { ClimbingRoute } from '../routes/types'
import { RouteIdentity } from './RouteIdentity'
import { routeDiscipline, type CompetitionRouteRef } from './types'

interface Props {
  isOpen: boolean
  competitionId: string
  /** A combined competition picks from both, one discipline at a time. */
  discipline: CompetitionDiscipline
  /** Already-attached routes, so the picker opens pre-ticked and in order. */
  current: CompetitionRouteRef[]
  /** The competition's sections, to tell which wall an attached route is on. */
  sections?: { id: string; discipline: string | null }[]
  onClose: () => void
  onSaved: () => void
}

/**
 * Groups routes under their section, newest section first, with unsectioned
 * routes last. Mirrors groupBySection on the member Routes page so setters see
 * the same ordering in both places.
 */
function groupBySection(routes: ClimbingRoute[], noSectionLabel: string) {
  const bySection = new Map<string, { id: string | null; name: string; routes: ClimbingRoute[] }>()

  for (const route of routes) {
    const key = route.section?.id ?? '__none__'
    if (!bySection.has(key)) {
      bySection.set(key, {
        id: route.section?.id ?? null,
        name: route.section?.name ?? noSectionLabel,
        routes: [],
      })
    }
    bySection.get(key)!.routes.push(route)
  }

  const groups = [...bySection.values()]
  const newest = (g: (typeof groups)[number]) =>
    Math.max(...g.routes.map((r) => new Date(r.created_at).getTime()))

  return groups.sort((a, b) => {
    // Unsectioned routes always sink to the bottom; the rest go newest first.
    if (a.id === null) return 1
    if (b.id === null) return -1
    return newest(b) - newest(a)
  })
}

export function RoutePickerModal({
  isOpen,
  competitionId,
  discipline,
  current,
  sections = [],
  onClose,
  onSaved,
}: Props) {
  const { getAccessTokenSilently } = useAuth()
  const { t } = useTranslation()
  const gradeConfig = useGymGradeConfig()
  const apiUrl = import.meta.env.VITE_API_URL as string

  const [routes, setRoutes] = useState<ClimbingRoute[]>([])
  const [loading, setLoading] = useState(true)
  const [selected, setSelected] = useState<string[]>([])
  const [gradeFilter, setGradeFilter] = useState<number | null>(null)
  const [saving, setSaving] = useState(false)
  const boards = competitionBoards(discipline)
  // Which wall the list shows. Only switchable when the competition is combined.
  const [board, setBoard] = useState<Discipline>(boards[0])
  // Flipping between the two walls quickly must not let the slower response
  // overwrite the list for the wall now showing.
  const requestRef = useRef(0)

  const load = useCallback(async () => {
    const request = ++requestRef.current
    setLoading(true)
    try {
      const token = await getAccessTokenSilently()
      const res = await fetch(
        `${apiUrl}/climbing-routes?status=active&limit=200&discipline=${board}`,
        { headers: { Authorization: `Bearer ${token}` } },
      )
      if (res.ok) {
        const body = (await res.json()) as { routes: ClimbingRoute[] } | ClimbingRoute[]
        if (request === requestRef.current) setRoutes(Array.isArray(body) ? body : body.routes)
      }
    } finally {
      if (request === requestRef.current) setLoading(false)
    }
  }, [apiUrl, board, getAccessTokenSilently])

  useEffect(() => {
    // Back to the first wall on close, so the next open loads the right list once.
    if (!isOpen) {
      setBoard(competitionBoards(discipline)[0])
      return
    }
    setSelected([...current].sort((a, b) => a.sort_order - b.sort_order).map((r) => r.route_id))
  }, [isOpen, current, discipline])

  // Kept apart from the reset above: switching between bouldering and lead
  // reloads the list but must keep what was already ticked on the other wall.
  useEffect(() => {
    if (!isOpen) return
    setGradeFilter(null)
    void load()
  }, [isOpen, load])

  // Only grades the gym actually has routes at — an empty filter chip is noise.
  const availableGrades = useMemo(() => {
    const counts = new Map<number, number>()
    for (const r of routes) counts.set(r.grade, (counts.get(r.grade) ?? 0) + 1)
    return gradesForDiscipline(gradeConfig, board)
      .filter((g) => counts.has(g.rank))
      .map((g) => ({ ...g, count: counts.get(g.rank) ?? 0 }))
  }, [routes, gradeConfig, board])

  const visible = useMemo(
    () => (gradeFilter === null ? routes : routes.filter((r) => r.grade === gradeFilter)),
    [routes, gradeFilter],
  )

  // Headers name the section; the one case worth hiding is a gym with no
  // sections at all, where every row would carry the same placeholder.
  const groups = useMemo(() => groupBySection(visible, t('routes.noSection')), [visible, t])

  // Which wall a newly ticked route came from. A route already in the set is
  // placed by its section instead.
  const [pickedOn, setPickedOn] = useState<Record<string, Discipline>>({})

  function toggle(routeId: string) {
    setPickedOn((prev) => ({ ...prev, [routeId]: board }))
    setSelected((prev) =>
      prev.includes(routeId) ? prev.filter((id) => id !== routeId) : [...prev, routeId],
    )
  }

  function disciplineOfId(routeId: string): Discipline {
    const ref = current.find((c) => c.route_id === routeId)
    if (ref) return routeDiscipline(discipline, ref.route?.section_id, sections)
    return pickedOn[routeId] ?? boards[0]
  }

  /** Position among the picks on the same wall: bouldering 1..n, lead 1..n. */
  function orderOnWall(routeId: string): number {
    const wall = disciplineOfId(routeId)
    return selected.filter((id) => disciplineOfId(id) === wall).indexOf(routeId)
  }

  async function save() {
    // A new pick is numbered after the highest number already on its wall, so
    // a combined competition runs bouldering 1..n and lead 1..n.
    const highest: Partial<Record<Discipline, number>> = {}
    for (const routeId of selected) {
      const n = Number.parseInt(current.find((c) => c.route_id === routeId)?.label ?? '', 10)
      const wall = disciplineOfId(routeId)
      if (Number.isFinite(n)) highest[wall] = Math.max(highest[wall] ?? 0, n)
    }
    const newLabels = new Map<string, string>()
    for (const routeId of selected) {
      if (current.some((c) => c.route_id === routeId)) continue
      const wall = disciplineOfId(routeId)
      highest[wall] = (highest[wall] ?? 0) + 1
      newLabels.set(routeId, String(highest[wall]))
    }

    setSaving(true)
    try {
      const token = await getAccessTokenSilently()
      // Replace the whole set in one call. Labels follow pick order, which is
      // the order climbers read them in. A route already in the set keeps the
      // round it was assigned to, because this is a replace and anything not
      // sent back is dropped; a newly picked one starts on the whole
      // competition and gets assigned from the Routes tab.
      const res = await fetch(`${apiUrl}/competitions/${competitionId}/routes`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          routes: selected.map((routeId, index) => {
            const existing = current.find((c) => c.route_id === routeId)
            return {
              route_id: routeId,
              label: existing?.label ?? newLabels.get(routeId) ?? `${index + 1}`,
              sort_order: index,
              round_id: existing?.round_id ?? null,
            }
          }),
        }),
      })
      if (res.ok) {
        onSaved()
        onClose()
      }
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="2xl" scrollBehavior="inside">
      <ModalContent>
        <ModalHeader className="flex flex-col gap-1">
          <span>{t('competitions.pickRoutes')}</span>
          <span className="text-sm font-normal text-foreground/60">
            {t('competitions.pickRoutesHint')}
          </span>
        </ModalHeader>

        <ModalBody className="gap-3">
          {boards.length > 1 && (
            <Tabs
              aria-label={t('competitions.fieldDiscipline')}
              selectedKey={board}
              onSelectionChange={(key) => setBoard(key as Discipline)}
            >
              {boards.map((d) => (
                <Tab key={d} title={t(`competitions.discipline_${d}`)} />
              ))}
            </Tabs>
          )}

          {availableGrades.length > 1 && (
            <div className="flex flex-wrap gap-1.5">
              <button
                type="button"
                onClick={() => setGradeFilter(null)}
                className={`rounded-full px-3 py-1 text-xs font-semibold transition-colors ${
                  gradeFilter === null
                    ? 'bg-foreground text-background'
                    : 'bg-default-100 text-foreground/70 hover:bg-default-200'
                }`}
              >
                {t('competitions.allGrades', { count: routes.length })}
              </button>
              {availableGrades.map((g) => {
                const badge = softBadgeColors(g.color)
                const active = gradeFilter === g.rank
                return (
                  <button
                    key={g.rank}
                    type="button"
                    onClick={() => setGradeFilter(active ? null : g.rank)}
                    className={`rounded-full px-3 py-1 text-xs font-bold transition-all ${
                      active ? 'ring-2 ring-foreground ring-offset-1' : 'hover:opacity-80'
                    }`}
                    style={{ backgroundColor: badge.bg, color: badge.fg }}
                  >
                    {g.label} <span className="opacity-60">{g.count}</span>
                  </button>
                )
              })}
            </div>
          )}

          {loading ? (
            <div className="flex justify-center py-10">
              <Spinner />
            </div>
          ) : visible.length === 0 ? (
            <p className="py-10 text-center text-foreground/60">
              {routes.length === 0
                ? t('competitions.noPoolRoutes')
                : t('competitions.noRoutesAtGrade')}
            </p>
          ) : (
            <div className="flex flex-col gap-4">
              {groups.map((group) => (
                <div key={group.id ?? '__none__'} className="flex flex-col gap-1.5">
                  {(groups.length > 1 || group.id !== null) && (
                    <p className="text-xs font-semibold uppercase tracking-wide text-foreground/50">
                      {group.name}
                    </p>
                  )}
                  <ul className="flex flex-col gap-1.5">
                    {group.routes.map((route) => {
                      const order = selected.includes(route.id) ? orderOnWall(route.id) : -1
                      const isSelected = order !== -1

                      return (
                        <li key={route.id}>
                          <button
                            type="button"
                            onClick={() => toggle(route.id)}
                            aria-pressed={isSelected}
                            className={`flex w-full items-center gap-3 rounded-lg border p-2 text-left transition-colors ${
                              isSelected
                                ? 'border-primary bg-primary/5'
                                : 'border-divider bg-content1 hover:bg-content2'
                            }`}
                          >
                            {/* Selection order doubles as the tick — the number is
                              the label climbers will see on the wall. */}
                            <span
                              className={`flex h-7 w-7 flex-none items-center justify-center rounded-full text-xs font-bold ${
                                isSelected
                                  ? 'bg-primary text-white'
                                  : 'border-2 border-default-300 text-transparent'
                              }`}
                            >
                              {isSelected ? order + 1 : '0'}
                            </span>

                            <RouteIdentity route={route} apiUrl={apiUrl} discipline={board} />
                          </button>
                        </li>
                      )
                    })}
                  </ul>
                </div>
              ))}
            </div>
          )}
        </ModalBody>

        <ModalFooter className="justify-between">
          <span className="self-center text-sm text-foreground/60">
            {t('competitions.selectedCount', { count: selected.length })}
          </span>
          <div className="flex gap-2">
            <Button variant="light" onPress={onClose} isDisabled={saving}>
              {t('common.cancel')}
            </Button>
            <Button color="primary" isLoading={saving} onPress={() => void save()}>
              {t('competitions.saveRoutes', { count: selected.length })}
            </Button>
          </div>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
